/**
 * Resolve facet parameters server-side, the way `?preset=` already is.
 *
 * A facet deep link answered 200 and served the **unfiltered** hub to anything
 * without JavaScript — which is what a crawler is, and what a visitor gets
 * before hydration. The server-rendered HTML for
 * `/documents?series=200%20Information%20and%20Analysis` was byte-comparable to
 * bare `/documents`: same 25 rows, same "Show 1,762 results", same meta
 * description. Six facets on the directives documents hub
 * (`document_category`, `status`, `series`, `opi`, `subject_areas`,
 * `effective`) made that a combinatorial address space over a single rendered
 * body. See BL-0047.
 *
 * WHY THIS DELEGATES TO CORE RATHER THAN BUILDING CRITERIA ITSELF
 *
 * Each facet type turns a value into a query criterion its own way —
 * `selection.is` for a single select, `list.contains` for a multiple, and
 * `date.between` for a date range, which additionally rejects anything that is
 * not a pair of strings. Reimplementing that mapping here would be a second
 * implementation of a rule the client already owns, and the two would drift on
 * the next Volto upgrade with no test able to see it: the symptom is a
 * server-rendered page that quietly disagrees with the client-rendered one.
 *
 * So the widget's own `valueToQuery` is resolved through core's extension
 * registry, exactly as `withSearch.jsx` does. Core is imported, never modified.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 *
 * It does not touch the canonical. `BrowseSEO` builds that from `preset` and
 * the page number alone, on purpose, so facet and sort state cannot mint
 * indexable near-duplicates. Serving the correct body and consolidating the URL
 * are not in tension: the page a visitor or crawler receives is now the page
 * they asked for, and the address still points at the hub. Making the facet
 * space indexable is a separate bet that should not be taken while there is no
 * Search Console instrument to measure it (BL-0027).
 */
import { resolveExtension } from '@plone/volto/helpers/Extensions/withBlockExtensions';
import config from '@plone/volto/registry';

/** Parameters that are page state, never facet values. */
const RESERVED = new Set(['preset', 'page', 'query', 'sort_on', 'sort_order']);

/**
 * The value for one facet, read out of the URL.
 *
 * `URLSearchParams` rather than core's `query-string`: it is a builtin, so this
 * module has no dependency the addon does not already resolve, and `getAll`
 * states the repeated-parameter rule outright instead of leaving it to a
 * library's collapse-one-to-a-scalar convention.
 *
 * That convention is the thing to get right. A `multiple` facet given a single
 * value must still become an array, or it produces `selection.is` where the
 * client produces `list.contains` and the two renders disagree on a page that
 * looks fine.
 */
export const facetValueFrom = (params, facet) => {
  const name = facet?.field?.value;
  if (!name || RESERVED.has(name)) return undefined;
  const all = params.getAll(name).filter((v) => v !== '');
  if (!all.length) return undefined;
  if (facet.multiple) return all;
  // A date range is the one non-multiple facet that legitimately carries a
  // pair; its widget rejects anything that is not two strings.
  if (facet.type === 'daterangeFacet') return all;
  return all[0];
};

/**
 * Criteria for whichever of the block's declared facets the URL actually sets.
 * Facets with no value in the URL contribute nothing, so a hub with no facet
 * parameters produces exactly the query it produced before this file existed.
 */
export const facetCriteriaFrom = (data, search) => {
  const facets = data?.facets || [];
  if (!facets.length || !search) return [];

  const widgets =
    config.blocks?.blocksConfig?.search?.extensions?.facetWidgets?.types;
  if (!widgets) return [];

  const params = new URLSearchParams(search);

  return facets
    .map((facet) => {
      if (!facet?.field?.value) return null;
      const value = facetValueFrom(params, facet);
      if (value === undefined) return null;
      return criterionFor(widgets, facet, value);
    })
    .filter(Boolean);
};

/**
 * One facet value -> its query row, through the facet widget's own
 * `valueToQuery`, or null.
 */
const criterionFor = (widgets, facet, value) => {
  let valueToQuery;
  try {
    ({ valueToQuery } = resolveExtension('type', widgets, facet) || {});
  } catch {
    return null;
  }
  if (typeof valueToQuery !== 'function') return null;
  try {
    return valueToQuery({ value, facet }) || null;
  } catch {
    // A malformed parameter is a visitor typing in the address bar, not an
    // error worth failing the render for. Fall through to the unfiltered
    // page, which is what shipped before.
    return null;
  }
};

/**
 * The rows the browser's first render ADDS for facets whose index the block's
 * own base query already filters on, reproduced so the server seeds the same
 * request the hydration pass is about to make. The hydration pass skips a
 * request the server already answered (helpers/querystringKey), and it can
 * only recognise one that is identical.
 *
 * Core's withSearch (kept verbatim in our shadow) seeds each configured
 * facet's state from a base-query row on the same index
 * (`intializeFacetWithQueryValue`), and `getInitialState` then APPENDS that
 * facet's `valueToQuery` row to the base query. So a block whose base query
 * filters on a faceted index sends that filter twice. Every directives series
 * preset does: the preset adds `series selection.any [X]` and the `series`
 * facet adds `series list.contains [X]`. The two rows select the same
 * documents, so the result set does not change; only the request does, and
 * that alone made every series page re-request its results on load while the
 * bare hubs did not [Chrome, both queryKeys read from the live store,
 * 2026-09-27].
 *
 * A facet the URL sets is left out: `facetCriteriaFrom` handles it, and on
 * the client the URL value replaces the base value for that facet.
 */
export const baseFacetCriteria = (data, search) => {
  const facets = data?.facets || [];
  const base = data?.query?.query || [];
  if (!facets.length || !base.length) return [];

  const widgets =
    config.blocks?.blocksConfig?.search?.extensions?.facetWidgets?.types;
  if (!widgets) return [];

  const params = new URLSearchParams(search || '');

  return facets
    .map((facet) => {
      const name = facet?.field?.value;
      if (!name) return null;
      const row = base.find((c) => c?.i === name);
      if (!row || facetValueFrom(params, facet) !== undefined) return null;
      return criterionFor(widgets, facet, row.v);
    })
    .filter(Boolean);
};

/**
 * Merge facet criteria into a block query.
 *
 * A criterion from the URL REPLACES a base-query criterion on the same index,
 * rather than being appended alongside it — two criteria on one index would
 * intersect, so a preset that already constrains `status` plus a visitor
 * choosing a different `status` would return nothing instead of what they
 * asked for. The client resolves this the same way: `normalizeState` strips
 * configured facets out of the base query before adding the facet values.
 *
 * Returns the input untouched when there is nothing to merge, so the no-facet
 * path keeps its identity and not merely its value.
 */
export const withFacets = (query, criteria) => {
  if (!criteria?.length) return query;
  const overridden = new Set(criteria.map((c) => c.i));
  return {
    ...query,
    query: [
      ...(query?.query || []).filter((c) => !overridden.has(c.i)),
      ...criteria,
    ],
  };
};
